import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DataTable from '../../components/DataTable';
import Teeth from './Teeth';
import {
  deleteCustomBracketSetAction,
  fetchCustomBracketSetsAction,
  openCustomBracketSetForm
} from '../../reducers/customBracketSets';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import StatusTag from '../../components/StatusTag';
import hasPermission from '../../selectors/hasPermission';

const CREATOR_TYPE_MANAGEMENT = 1;
const CREATOR_TYPE_ORGANIZATION = 2;
const RESTRICTION_TYPE_CREATOR_ONLY = 1;

const isOnlyForUser = (row, userId) =>
  Boolean(userId) &&
  row.restriction_type === RESTRICTION_TYPE_CREATOR_ONLY &&
  row.user_id === userId;

const creatorTypeTag = creatorType => {
  if (creatorType === CREATOR_TYPE_ORGANIZATION) {
    return { label: 'Organization', color: 'blue' };
  }
  if (creatorType === CREATOR_TYPE_MANAGEMENT) {
    return { label: 'Management', color: 'grey' };
  }
  return null;
};

const List = ({
  canManageAll,
  currentUserId,
  isManagement,
  fetching,
  rows,
  fetch,
  total,
  parameters,
  onEdit,
  deleting,
  onDuplicate,
  onDelete
}) => {
  const defaultRowsPerPage = 10;
  const [deletingItem, setDeletingItem] = React.useState(null);

  const handleFetch = useCallback(
    params => {
      const additionalParams = canManageAll ? {} : { created_by_organization: true };
      return fetch({ ...params, ...additionalParams, exclude_pending_creation: true });
    },
    [canManageAll, fetch]
  );

  const columns = [
    {
      id: 'name',
      label: 'Name',
      formatMethod: (value, row) => {
        const tags = [
          canManageAll ? creatorTypeTag(row.creator_type) : null,
          !isManagement && isOnlyForUser(row, currentUserId)
            ? { label: 'Own', color: 'purple' }
            : null
        ].filter(Boolean);
        return (
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <span>{value}</span>
            {tags.length > 0 && (
              <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginTop: 4 }}>
                {tags.map(tag => (
                  <StatusTag
                    key={tag.label}
                    label={tag.label}
                    color={tag.color}
                    style={{ marginLeft: 0 }}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      }
    },
    {
      id: 'teeth',
      label: 'Brackets',
      tableCellProps: {
        align: 'center'
      },
      disabledSorting: true,
      formatMethod: value => {
        return <Teeth size="small" data={value} />;
      }
    }
  ];

  const canModify = row => canManageAll || row.creator_type === CREATOR_TYPE_ORGANIZATION;

  const handleDelete = answer => {
    if (!answer) {
      setDeletingItem(null);
      return;
    }
    onDelete(deletingItem.id).then(() => setDeletingItem(null));
  };

  return (
    <>
      <ConfirmationDialog
        open={deletingItem !== null}
        title="Removal"
        bodyText={`Are you sure you want to remove "${deletingItem?.name}"?`}
        cancelButtonProps={{
          disabled: deleting
        }}
        confirmButtonProps={{
          disabled: deleting
        }}
        confirmText="Yes"
        handleClose={handleDelete}
      />
      <Box display="flex" justifyContent="center" m={2}>
        <DataTable
          isLoading={fetching}
          rows={rows}
          columns={columns}
          updateData={handleFetch}
          total={total}
          defaultRowsPerPage={defaultRowsPerPage}
          defaultOrderBy=""
          actionCellProps={{
            align: 'center',
            style: { whiteSpace: 'nowrap' }
          }}
          globalParams={parameters}
          customActions={[
            {
              name: 'Edit',
              handleOnAction: (value, row) => {
                onEdit(row);
              },
              text: 'Edit',
              color: 'primary',
              title: 'Edit',
              icon: <EditIcon />,
              isIconButton: true,
              condition: canModify
            },
            {
              name: 'Delete',
              handleOnAction: (value, row) => setDeletingItem(row),
              text: 'Delete',
              color: 'secondary',
              title: 'Delete',
              icon: <DeleteIcon />,
              isIconButton: true,
              condition: canModify
            },
            {
              name: 'Duplicate',
              handleOnAction: (value, row) => onDuplicate({ ...row, id: undefined, name: '' }),
              text: 'duplicate',
              title: 'Duplicate',
              icon: <FileCopyIcon />,
              isIconButton: true
            }
          ]}
        />
      </Box>
    </>
  );
};

List.propTypes = {
  fetching: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetch: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  parameters: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  deleting: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  canManageAll: PropTypes.bool.isRequired,
  currentUserId: PropTypes.number,
  isManagement: PropTypes.bool,
  onDuplicate: PropTypes.func.isRequired
};

List.defaultProps = {
  currentUserId: null,
  isManagement: false
};

export default connect(
  (state, ownProps) => {
    const { organizationId } = ownProps;
    const customSetState = state.customBracketSets;
    const listState =
      customSetState.list.organizationId === organizationId ? customSetState.list : {};

    const rows = (listState?.data || []).filter(row => !row.creation_request_status);

    return {
      canManageAll: hasPermission(state, {
        permissions: ['custom-bracket-sets.manage'],
        organizationId
      }),
      currentUserId: state.auth.user?.id ?? null,
      isManagement: state.auth.isManagement,
      fetching: listState.loading || false,
      deleting: customSetState.deleting,
      rows: rows || [],
      total: listState.total || 0,
      parameters: customSetState.parameters
    };
  },
  (dispatch, ownProps) => {
    return {
      fetch: (params = {}) => {
        return dispatch(fetchCustomBracketSetsAction(ownProps.organizationId, params));
      },
      onEdit: data => dispatch(openCustomBracketSetForm(data)),
      onDelete: id => dispatch(deleteCustomBracketSetAction(ownProps.organizationId, id)),
      onDuplicate: data => dispatch(openCustomBracketSetForm(data))
    };
  }
)(List);
